import { createSignal, onCleanup, onMount } from "solid-js";

import type { Client, User } from "stoat.js";

import {
  type ActivityGame,
  type FriendsActivity,
  konkordVoice,
} from "./konkordVoice";

/**
 * Konkord: friends' activity (what they play now and this week)
 *
 * The desktop app finds the game we're playing and the page sends it to our
 * gateway only when it starts, stops, or once a minute while it runs. Pages
 * showing friends' games ask the gateway once a minute while visible.
 */

type NativeActivity = {
  current(): Promise<ActivityGame | null>;
  onGame(callback: (game: ActivityGame | null) => void): () => void;
};

const HEARTBEAT_MS = 60_000;
const REFRESH_MS = 60_000;

const [myGame, setMyGame] = createSignal<ActivityGame | null>(null);
const [activity, setActivity] = createSignal<FriendsActivity>({
  now: [],
  week: [],
});

/**
 * The game we're playing (desktop app only)
 */
export { myGame };

let shared = false;
let stopFollowing: (() => void) | undefined;

/**
 * Send our game while it runs, or clear it once it stopped
 */
function send(game: ActivityGame | null) {
  if (!game && !shared) return;
  konkordVoice.postActivity(game).then(
    () => (shared = !!game),
    () => {
      // tried again on the next change or heartbeat
    },
  );
}

/**
 * Follow the game we're playing (desktop app) and share it with friends
 * ("share"), stop (logged out) or stop and make the gateway forget our games
 * (the user turned sharing off)
 * @param mode What to do
 */
export function setActivitySharing(mode: "share" | "stop" | "forget") {
  stopFollowing?.();
  stopFollowing = undefined;

  if (mode !== "share") {
    setMyGame(null);
    if (mode === "forget") {
      konkordVoice.postActivity(null, true).then(
        () => (shared = false),
        () => {
          // the gateway drops a game without heartbeat after 3 minutes
        },
      );
    }
    return;
  }

  const api = (window as { native?: { activity?: NativeActivity } }).native
    ?.activity;
  if (!api) return;

  let active = true;
  const update = (game: ActivityGame | null) => {
    if (!active) return;
    setMyGame(game);
    send(game);
  };

  void api.current().then(update, () => update(null));
  const unsubscribe = api.onGame(update);
  const timer = setInterval(() => {
    const game = myGame();
    if (game) send(game);
  }, HEARTBEAT_MS);

  stopFollowing = () => {
    active = false;
    unsubscribe();
    clearInterval(timer);
  };
}

let watchers = 0;
let refreshTimer: ReturnType<typeof setInterval> | undefined;
let friendIds: () => string[] = () => [];

/**
 * Ask the gateway for friends' activity (one request for every page showing it)
 */
async function refresh() {
  if (document.hidden) return;
  const result = await konkordVoice
    .friendsActivity(friendIds())
    .catch(() => undefined);
  if (result) setActivity(result);
}

/**
 * Friends' activity, refreshed while a component using it is mounted
 * @param client Client
 * @returns Latest activity
 */
export function useFriendsActivity(client: () => Client) {
  onMount(() => {
    friendIds = () =>
      client()
        .users.toList()
        .filter((user) => user.relationship === "Friend")
        .map((user) => user.id);

    if (watchers++ === 0) {
      void refresh();
      refreshTimer = setInterval(() => void refresh(), REFRESH_MS);
      document.addEventListener("visibilitychange", refresh);
    }

    onCleanup(() => {
      if (--watchers === 0) {
        clearInterval(refreshTimer);
        document.removeEventListener("visibilitychange", refresh);
      }
    });
  });

  return activity;
}

/**
 * Konkord: the game someone is playing, for their profile card. Reads what the
 * friends' activity already knows, and asks our gateway at most once a minute
 * however many cards are opened.
 * @param client Client
 * @param user Who
 * @returns Their game, or our own on our card
 */
export function friendGame(client: () => Client, user: User) {
  if (user.self) return myGame() ?? undefined;
  if (user.relationship !== "Friend") return undefined;
  if (!watchers && Date.now() - lastAsked > REFRESH_MS) {
    lastAsked = Date.now();
    friendIds = () =>
      client()
        .users.toList()
        .filter((friend) => friend.relationship === "Friend")
        .map((friend) => friend.id);
    void refresh();
  }
  return activity().now.find((now) => now.user === user.id)?.game;
}

let lastAsked = 0;

/**
 * The URL if it is Steam art (https on *.steamstatic.com)
 * @param url URL
 * @returns URL, if it is one
 */
function steamArt(url?: string) {
  if (!url) return;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" &&
      parsed.hostname.endsWith(".steamstatic.com")
      ? parsed.href
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Pictures of a game to try in order: the Steam art our gateway found, Steam's
 * old fixed header (newer games don't have one), then the program's icon
 * @param game Game
 * @returns Image URLs, best first
 */
export function gameCovers(game: ActivityGame) {
  const urls = [
    steamArt(game.cover),
    game.steamAppId
      ? `https://cdn.cloudflare.steamstatic.com/steam/apps/${game.steamAppId}/header.jpg`
      : undefined,
    game.icon,
  ];
  return urls.filter(
    (url, index): url is string => !!url && urls.indexOf(url) === index,
  );
}

/**
 * Short duration ("1 h 12 min", "45 min")
 * @param ms Milliseconds
 * @returns Text
 */
export function playTime(ms: number) {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest && hours < 10 ? `${hours} h ${rest} min` : `${hours} h`;
}
